import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { Row, Col, Button, message } from 'antd';
import { Input, Select } from 'formik-antd';
import { UploadOutlined, CloseOutlined, FilePdfOutlined, EyeOutlined } from '@ant-design/icons';
import { useFormikContext } from 'formik';
import styled from 'styled-components';
import { Box, Text, FormInput } from 'atoms';
import { getSignedUrl } from 'store/commonSlice';
import FilePreviewModal from 'componentsv2/common/FilePreviewModal';

const { Option } = Select;

// Constants
const UPLOADING_TEXT = 'Uploading...';
const BROWSE_FILE_TEXT = 'Browse File';

const DisabledInput = styled(Input)`
	background-color: #c6c6c8 !important;
	color: #fff !important;
	font-weight: 500;
	cursor: not-allowed;

	&::placeholder {
		color: #fff !important;
	}
`;

const BrowseButton = styled(Button)`
	background-color: #18405d;
	color: #fff;
	border: none;
	border-radius: 4px;
	padding: 8px 20px;
	height: 40px;
	font-size: 14px;
	font-weight: 500;
	display: flex;
	align-items: center;
	gap: 8px;
	width: 100%;

	&:hover {
		background-color: #153e60 !important;
		color: #fff !important;
	}
`;

const FileItem = styled(Box)`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 8px 12px;
	background-color: #f5f5f5;
	border-radius: 4px;
	margin-top: 8px;
	gap: 8px;
`;

const ErrorText = styled(Text)`
	color: #ff4d4f;
	font-size: 12px;
	margin-top: 4px;
	display: block;
`;

const FileUploadContainer = styled(Box)`
	margin-top: 23px;
`;

/**
 * Extract filename from S3 key by removing UUID prefix
 * S3 keys are in format: {uuid}-{filename}
 * Example: "340d1b25-c372-439e-b92c-5306e4748a3-Data_Analytics_Assignment.pdf"
 * Returns: "Data_Analytics_Assignment.pdf"
 */
const extractFilenameFromS3Key = (s3Key) => {
	if (!s3Key) return '';
	// UUID format: 8-4-4-4-12 characters (36 chars total + 1 hyphen)
	const uuidPattern = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}-/i;
	const match = s3Key.match(uuidPattern);
	if (match) {
		return s3Key.substring(match[0].length);
	}
	// If no UUID pattern found, return the original key
	return s3Key;
};

export default function BusinessInformationStep({ isEditable = true, isDisabled = false }) {
	const dispatch = useDispatch();
	const { setFieldValue, values, errors, touched, setFieldTouched } = useFormikContext();
	const disabled = !isEditable || isDisabled;

	// File states
	const [panFile, setPanFile] = useState(null);
	const [gstFile, setGstFile] = useState(null);
	const [aadharFile, setAadharFile] = useState(null);

	// Upload loading states
	const [uploading, setUploading] = useState({
		pan: false,
		gst: false,
		aadhar: false,
		udf: false,
	});

	// File preview modal state
	const [previewModal, setPreviewModal] = useState({
		visible: false,
		fileKey: null,
		fileName: '',
	});

	const panFileInputRef = useRef(null);
	const gstFileInputRef = useRef(null);
	const aadharFileInputRef = useRef(null);

	// Restore file displays from Formik values when component mounts or values change
	useEffect(() => {
		// Restore PAN file if key exists but file state doesn't match
		if (values.pan_file_key && (!panFile || panFile.name !== values.pan_file_key)) {
			setPanFile({
				name: values.pan_file_key,
				size: 0, // Size unknown for existing files
			});
		} else if (!values.pan_file_key && panFile) {
			// Clear file if key is removed
			setPanFile(null);
		}

		// Restore GST file if key exists but file state doesn't match
		if (values.gst_file_key && (!gstFile || gstFile.name !== values.gst_file_key)) {
			setGstFile({
				name: values.gst_file_key,
				size: 0,
			});
		} else if (!values.gst_file_key && gstFile) {
			setGstFile(null);
		}

		// Restore Aadhar file if key exists but file state doesn't match
		if (values.aadhar_file_key && (!aadharFile || aadharFile.name !== values.aadhar_file_key)) {
			setAadharFile({
				name: values.aadhar_file_key,
				size: 0,
			});
		} else if (!values.aadhar_file_key && aadharFile) {
			setAadharFile(null);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [values.pan_file_key, values.gst_file_key, values.aadhar_file_key]);

	const handleFileSelect = (fieldName, fileInputRef) => {
		fileInputRef.current?.click();
	};

	const handleFileChange = async (event, fieldName, setFileState) => {
		const file = event.target.files?.[0];
		if (!file) return;

		// Validate file size (max 10MB)
		if (file.size > 10 * 1024 * 1024) {
			message.error('File size must be less than 10MB');
			return;
		}

		// Set uploading state
		const uploadKey = fieldName.replace('_file', '');
		setUploading((prev) => ({ ...prev, [uploadKey]: true }));

		try {
			// Step 1: Get signed URL
			const signedUrlResponse = await dispatch(
				getSignedUrl({
					url_type: 'upload',
					data: {
						entity: 'vendor',
						keys: [file.name],
					},
				})
			);

			const fileData = signedUrlResponse?.data?.[0];
			if (!fileData?.url || !fileData?.filename) {
				throw new Error('Failed to get signed URL');
			}

			// Step 2: Upload file to S3
			await fetch(fileData.url, {
				method: 'PUT',
				body: file,
				headers: {
					'Content-Type': file.type,
				},
			});

			// Step 3: Store file key in formik and set file state
			const fileKeyField = `${fieldName}_key`;
			setFieldValue(fileKeyField, fileData.filename);
			setFileState(file);

			message.success(`Document uploaded successfully`);
		} catch (error) {
			console.error('File upload error:', error);
			message.error(`Failed to upload document`);
		} finally {
			setUploading((prev) => ({ ...prev, [uploadKey]: false }));
			// Reset file input
			if (event.target) {
				event.target.value = '';
			}
		}
	};

	const handleFileRemove = (fieldName, setFileState) => {
		const fileKeyField = `${fieldName}_key`;
		setFieldValue(fileKeyField, '');
		setFileState(null);
		// Mark field as touched to show validation error immediately
		setFieldTouched(fileKeyField, true);
	};

	const handleViewFile = (fileKey, fileName) => {
		if (!fileKey) {
			message.error('No file to view');
			return;
		}
		setPreviewModal({
			visible: true,
			fileKey,
			fileName: fileName || extractFilenameFromS3Key(fileKey),
		});
	};

	const handleClosePreview = () => {
		setPreviewModal({
			visible: false,
			fileKey: null,
			fileName: '',
		});
	};

	return (
		<>
			{/* Hidden file inputs */}
			<input
				type="file"
				ref={panFileInputRef}
				style={{ display: 'none' }}
				accept=".pdf,.jpg,.jpeg,.png"
				disabled={disabled}
				onChange={(e) => !disabled && handleFileChange(e, 'pan_file', setPanFile)}
			/>
			<input
				type="file"
				ref={gstFileInputRef}
				style={{ display: 'none' }}
				accept=".pdf,.jpg,.jpeg,.png"
				disabled={disabled}
				onChange={(e) => !disabled && handleFileChange(e, 'gst_file', setGstFile)}
			/>
			<input
				type="file"
				ref={aadharFileInputRef}
				style={{ display: 'none' }}
				accept=".pdf,.jpg,.jpeg,.png"
				disabled={disabled}
				onChange={(e) => !disabled && handleFileChange(e, 'aadhar_file', setAadharFile)}
			/>

			{/* PAN Section */}
			<Row gutter={[24, 0]}>
				<Col span={4}>
					<FormInput label="Document Type" required name="pan_document_type">
						<DisabledInput name="pan_document_type" value="PAN" disabled />
					</FormInput>
				</Col>
				<Col span={8}>
					<FormInput label="PAN" required name="pan_number">
						<Input name="pan_number" placeholder="VCGCT1111A" disabled={disabled} />
					</FormInput>
				</Col>
				<Col span={12}>
					<FileUploadContainer>
						<BrowseButton
							icon={<UploadOutlined />}
							loading={uploading.pan}
							disabled={disabled}
							onClick={() =>
								!disabled && handleFileSelect('pan_file', panFileInputRef)
							}
						>
							{uploading.pan ? UPLOADING_TEXT : BROWSE_FILE_TEXT}
						</BrowseButton>
						{panFile && (
							<FileItem>
								<Box display="flex" alignItems="center" gap={8}>
									<FilePdfOutlined
										style={{ color: '#ff4d4f', fontSize: '16px' }}
									/>
									<Text fontSize={12} color="#1890ff">
										{extractFilenameFromS3Key(panFile.name)}
									</Text>
									<Text fontSize={12} color="#666">
										{panFile.size
											? `${(panFile.size / (1024 * 1024)).toFixed(1)}mb`
											: ''}
									</Text>
								</Box>
								<Box display="flex" alignItems="center" gap={8}>
									<EyeOutlined
										style={{
											cursor: 'pointer',
											color: '#1890ff',
											fontSize: '16px',
										}}
										onClick={() =>
											handleViewFile(values.pan_file_key, panFile.name)
										}
									/>
									{!disabled && (
										<CloseOutlined
											style={{ cursor: 'pointer', color: '#ff4d4f' }}
											onClick={() => handleFileRemove('pan_file', setPanFile)}
										/>
									)}
								</Box>
							</FileItem>
						)}
						{errors.pan_file_key && touched.pan_file_key && (
							<ErrorText>Please upload the PAN file</ErrorText>
						)}
					</FileUploadContainer>
				</Col>
			</Row>

			{/* GST Section */}
			<Row gutter={[24, 0]}>
				<Col span={4}>
					<FormInput label="Document Type" required name="gst_document_type">
						<DisabledInput name="gst_document_type" value="GST" disabled />
					</FormInput>
				</Col>
				<Col span={4}>
					<FormInput label="GST Type" required name="gst_type">
						<Select
							name="gst_type"
							placeholder="Select GST Type"
							disabled={disabled}
							onChange={(value) => {
								setFieldValue('gst_type', value);
								// Clear GST number and file when switching to unregistered
								if (value === 'Unregistered') {
									setFieldValue('gst_number', '');
									setFieldValue('gst_file_key', '');
									setGstFile(null);
								}
							}}
						>
							<Option value="Registered">Registered</Option>
							<Option value="Unregistered">Unregistered</Option>
						</Select>
					</FormInput>
				</Col>
				<Col span={7}>
					<FormInput
						label={values.gst_type === 'Registered' ? 'GST Number*' : 'GST Number'}
						required={values.gst_type === 'Registered'}
						name="gst_number"
					>
						<Input
							name="gst_number"
							placeholder="Enter GST Number"
							disabled={disabled || values.gst_type !== 'Registered'}
						/>
					</FormInput>
				</Col>
				<Col span={9}>
					<FileUploadContainer>
						<BrowseButton
							icon={<UploadOutlined />}
							loading={uploading.gst}
							disabled={disabled || values.gst_type === 'Unregistered'}
							onClick={() =>
								!disabled &&
								values.gst_type !== 'Unregistered' &&
								handleFileSelect('gst_file', gstFileInputRef)
							}
						>
							{uploading.gst ? UPLOADING_TEXT : BROWSE_FILE_TEXT}
						</BrowseButton>
						{gstFile && (
							<FileItem>
								<Box display="flex" alignItems="center" gap={8}>
									<FilePdfOutlined
										style={{ color: '#ff4d4f', fontSize: '16px' }}
									/>
									<Text fontSize={12} color="#1890ff">
										{extractFilenameFromS3Key(gstFile.name)}
									</Text>
									<Text fontSize={12} color="#666">
										{gstFile.size
											? `${(gstFile.size / (1024 * 1024)).toFixed(1)}mb`
											: ''}
									</Text>
								</Box>
								<Box display="flex" alignItems="center" gap={8}>
									<EyeOutlined
										style={{
											cursor: 'pointer',
											color: '#1890ff',
											fontSize: '16px',
										}}
										onClick={() =>
											handleViewFile(values.gst_file_key, gstFile.name)
										}
									/>
									{!disabled && (
										<CloseOutlined
											style={{ cursor: 'pointer', color: '#ff4d4f' }}
											onClick={() => handleFileRemove('gst_file', setGstFile)}
										/>
									)}
								</Box>
							</FileItem>
						)}
						{values.gst_type === 'Registered' &&
							errors.gst_file_key &&
							touched.gst_file_key && (
								<ErrorText>Please upload the GST file</ErrorText>
							)}
					</FileUploadContainer>
				</Col>
			</Row>

			{/* Aadhar Section */}
			<Row gutter={[24, 0]}>
				<Col span={4}>
					<FormInput label="Document Type" required name="aadhar_document_type">
						<DisabledInput name="aadhar_document_type" value="Aadhar" disabled />
					</FormInput>
				</Col>
				<Col span={8}>
					<FormInput label="Aadhar Number" required name="aadhar_number">
						<Input
							name="aadhar_number"
							placeholder="Enter Aadhar Number"
							disabled={disabled}
						/>
					</FormInput>
				</Col>
				<Col span={12}>
					<FileUploadContainer>
						<BrowseButton
							icon={<UploadOutlined />}
							loading={uploading.aadhar}
							disabled={disabled}
							onClick={() =>
								!disabled && handleFileSelect('aadhar_file', aadharFileInputRef)
							}
						>
							{uploading.aadhar ? UPLOADING_TEXT : BROWSE_FILE_TEXT}
						</BrowseButton>
						{aadharFile && (
							<FileItem>
								<Box display="flex" alignItems="center" gap={8}>
									<FilePdfOutlined
										style={{ color: '#ff4d4f', fontSize: '16px' }}
									/>
									<Text fontSize={12} color="#1890ff">
										{extractFilenameFromS3Key(aadharFile.name)}
									</Text>
									<Text fontSize={12} color="#666">
										{aadharFile.size
											? `${(aadharFile.size / (1024 * 1024)).toFixed(1)}mb`
											: ''}
									</Text>
								</Box>
								<Box display="flex" alignItems="center" gap={8}>
									<EyeOutlined
										style={{
											cursor: 'pointer',
											color: '#1890ff',
											fontSize: '16px',
										}}
										onClick={() =>
											handleViewFile(values.aadhar_file_key, aadharFile.name)
										}
									/>
									{!disabled && (
										<CloseOutlined
											style={{ cursor: 'pointer', color: '#ff4d4f' }}
											onClick={() =>
												handleFileRemove('aadhar_file', setAadharFile)
											}
										/>
									)}
								</Box>
							</FileItem>
						)}
						{errors.aadhar_file_key && touched.aadhar_file_key && (
							<ErrorText>Please upload the Aadhar file</ErrorText>
						)}
					</FileUploadContainer>
				</Col>
			</Row>

			{/* File Preview Modal */}
			<FilePreviewModal
				visible={previewModal.visible}
				fileKey={previewModal.fileKey}
				fileName={previewModal.fileName}
				entity="vendor"
				onClose={handleClosePreview}
			/>
		</>
	);
}

BusinessInformationStep.propTypes = {
	isEditable: PropTypes.bool,
	isDisabled: PropTypes.bool,
};
